import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H5,
  H6,
  Icon,
  ListItemIconTile,
  SingleFieldDialog,
  SingleFieldDialogImage,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import type { CustomerZoneListResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useNavigate } from "react-router";
import * as ROUTES from "../../constants/routes";
import { useCustomerZonesList } from "../../hooks/api/v2/customerZones/useCustomerZonesList";
import { useCustomerZonesDelete } from "../../hooks/api/v2/customerZones/useCustomerZonesDelete";
import { Delivery } from "./Delivery";
import { DeliveryZoneCreateDialog } from "./DeliveryZoneCreateDialog";
import { useUniqueDirectPostcodes } from "../customerZones/useUniqueDirectPostcodes";
import { useIsSellarAdmin } from "../../hooks/useIsSellarAdmin";
import { Alert } from "../../components/alert/Alert";
import { useCustomerZonesMigrateDeliveryZones } from "../../hooks/api/v2/customerZones/useCustomerZonesMigrateDeliveryZones";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useSupplier } from "../../hooks/api/v2/supplier/useSupplier";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { InfiniteList } from "~/components/infiniteLoading/InfiniteList";
import { useCustomerZonesCreate } from "~/hooks/api/v2/customerZones/useCustomerZonesCreate";
import { useDeliveryZoneMode } from "../../hooks/useDeliveryZoneMode";
import { RevertToBasicDeliveryZoneDialog } from "./components/DeliveryZoneModeDialogs";

export const DeliveryZones = () => {
  const navigate = useNavigate();
  const isAdmin = useIsSellarAdmin();
  const flags = useRemoteFeatureFlags();
  const fullyUnbundled = flags.isActive("unbundlingReleaseFinal");
  const [deleteTarget, setDeleteTarget] = useState<
    CustomerZoneListResponseType[number] | null
  >(null);
  const [createDialogActive, setCreateDialogActive] = useState(false);
  const [revertDialogActive, setRevertDialogActive] = useState(false);
  const customerZoneCreate = useCustomerZonesCreate();
  const deliveryZoneMode = useDeliveryZoneMode();

  const customerZonesQuery = useCustomerZonesList({
    limit: 10,
  });

  const customerZonesData = customerZonesQuery.data;

  const customerZones = customerZonesData?.pages.flatMap((p) => p) ?? [];

  const directZones = customerZones.filter(
    (cz) =>
      cz.type === "direct" && cz.systemDefinedType === null && !cz.default,
  );

  const nationwideZone =
    customerZones.find(
      (cz) => cz.type === "thirdParty" && cz.systemDefinedType === null,
    ) ?? null;

  return (
    <Delivery>
      <div className="flex flex-col gap-4">
        {isAdmin && <MigrateDeliveryZones />}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-1">
            <H5>Delivery zones</H5>
            <span className="text-zinc-500">
              Define what delivery information your customers see based on their
              location
            </span>
          </div>
          <div className="flex items-center gap-2">
            <DataButton
              className="flex items-center gap-1"
              border={true}
              onClick={() => setCreateDialogActive(true)}
            >
              <Icon icon="PLUS" />
              New
            </DataButton>
            {deliveryZoneMode.modeAvailable &&
              deliveryZoneMode.isAdvancedMode && (
                <Dropdown>
                  <DropdownButton>
                    <DataButton className="py-2.5" border={true}>
                      <Icon
                        icon="DOTS_VERTICAL"
                        className="text-black"
                        size={0.7}
                      />
                    </DataButton>
                  </DropdownButton>
                  <DropdownItems>
                    <DropdownItem
                      className="text-sm text-red-700"
                      onClick={() => setRevertDialogActive(true)}
                    >
                      Revert to basic mode
                    </DropdownItem>
                  </DropdownItems>
                </Dropdown>
              )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <InfiniteList
            query={customerZonesQuery}
            className="flex flex-col gap-2"
          >
            {directZones.map((customerZone) => (
              <DeliveryZoneListItem
                key={customerZone.id}
                title={customerZone.name ?? "Local"}
                postcodes={customerZone.CustomerZonePostcodes.map(
                  (pc) => pc.postcodeComponent,
                )}
                onClick={() =>
                  navigate(
                    ROUTES.DELIVERY_ZONE.href({
                      deliveryZoneId: customerZone.id,
                    }),
                  )
                }
                onEdit={() =>
                  navigate(
                    ROUTES.DELIVERY_ZONE.href({
                      deliveryZoneId: customerZone.id,
                    }),
                  )
                }
                onDelete={() => setDeleteTarget(customerZone)}
              />
            ))}
          </InfiniteList>
          {nationwideZone && (
            <DeliveryZoneListItem
              title={nationwideZone.name ?? "Nationwide"}
              description={
                nationwideZone.CourierDeliveryMethodId ||
                nationwideZone.PalletDeliveryMethodId
                  ? "All other postcodes"
                  : "No nationwide methods assigned"
              }
              postcodes={[]}
              onClick={() =>
                navigate(
                  ROUTES.DELIVERY_ZONE.href({
                    deliveryZoneId: nationwideZone.id,
                  }),
                )
              }
              onEdit={() =>
                navigate(
                  ROUTES.DELIVERY_ZONE.href({
                    deliveryZoneId: nationwideZone.id,
                  }),
                )
              }
            />
          )}
          {!nationwideZone && (fullyUnbundled || isAdmin) && (
            <>
              <Button
                loading={customerZoneCreate.isPending}
                onClick={() =>
                  customerZoneCreate.mutate({
                    type: "thirdParty",
                    name: "Nationwide",
                    default: true,
                    PriceTierId: null,
                    AvailabilityTierId: null,
                    OrderRulesId: null,
                  })
                }
                className="w-full"
              >
                Create Nationwide zone
              </Button>
            </>
          )}
        </div>
      </div>
      <DeliveryZoneDeleteDialog
        customerZone={deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
      />
      <DeliveryZoneCreateDialog
        active={createDialogActive}
        setActive={setCreateDialogActive}
      />
      <RevertToBasicDeliveryZoneDialog
        active={revertDialogActive}
        onDismiss={() => setRevertDialogActive(false)}
      />
    </Delivery>
  );
};

const DeliveryZoneListItem = ({
  title,
  description,
  postcodes,
  onClick,
  onEdit,
  onDelete,
}: {
  title: string;
  description?: string;
  postcodes: string[];
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) => {
  const formattedPostcodes = useUniqueDirectPostcodes(postcodes);
  const isDefaultZone = !onDelete;
  const subtitle =
    postcodes.length > 0
      ? formattedPostcodes.textList
      : isDefaultZone
        ? "All other postcodes"
        : "No postcodes";

  return (
    <Card
      onClick={onClick}
      className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon="MAP_MARKER" />
      <div className="flex w-full items-center gap-4 md:flex-nowrap">
        <div className="flex flex-col">
          <p className="font-semibold">{title}</p>
          <p className="text-sm text-zinc-500">{description ?? subtitle}</p>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          {onEdit ? (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={(e) => e.stopPropagation()}
            >
              <Dropdown>
                <DropdownButton>
                  <Button size="small" color="transparent">
                    <Icon icon="PENCIL" className="text-black" size={0.7} />
                  </Button>
                </DropdownButton>
                <DropdownItems>
                  <DropdownItem
                    className="border-b border-zinc-200 text-sm"
                    onClick={onEdit}
                  >
                    Edit
                  </DropdownItem>
                  {onDelete && (
                    <DropdownItem
                      className="border-b border-zinc-200 text-sm text-red-700"
                      onClick={onDelete}
                    >
                      Delete
                    </DropdownItem>
                  )}
                </DropdownItems>
              </Dropdown>
            </div>
          ) : (
            <Icon icon="CHEVRON_RIGHT" size={1} />
          )}
        </span>
      </div>
    </Card>
  );
};

const DeliveryZoneDeleteDialog = ({
  customerZone,
  onDismiss,
}: {
  customerZone: CustomerZoneListResponseType[number] | null;
  onDismiss: () => void;
}) => {
  const customerZoneDelete = useCustomerZonesDelete(customerZone?.id ?? 0);

  const [checked, setChecked] = useState(false);

  return (
    <SingleFieldDialog
      active={!!customerZone}
      onDismiss={onDismiss}
      size="large"
    >
      <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/app-assets/delivery-zone-delete-generic.png" />
      <SingleFieldDialogHeader
        title="Are you sure you want to delete this delivery zone?"
        description="Any new customers located in this delivery zone, and any customers currently inheriting their delivrey methods from this delivery zone will see your default 'Nationwide' delivery settings"
        icon="DELETE"
        iconClassName="bg-red-50 text-red-600"
      />
      <SingleFieldDialogCTA className="items-center">
        <label className="mr-auto ml-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I confirm I want to delete this delivery zone
        </label>

        <Button color="transparent" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button
          color="red"
          loading={customerZoneDelete.isPending}
          disabled={!customerZone || !checked}
          onClick={() =>
            customerZoneDelete.mutate(undefined, {
              onSuccess: onDismiss,
            })
          }
        >
          Delete
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const MigrateDeliveryZones = () => {
  const [checked, setChecked] = useState(false);
  const migrateZones = useCustomerZonesMigrateDeliveryZones();

  const { business } = useSellarCurrentBusiness();
  const { data: supplier } = useSupplier(business?.Supplier?.id);

  if (!supplier || supplier.hasMigratedDeliveryZones) return <></>;

  return (
    <Card className="border border-red-500 bg-white">
      <CardHeader className="flex flex-col bg-white p-4">
        <H5>
          Migrate this suppliers delivery area postcodes to delivery zones
        </H5>
        <H6>This can only be run once</H6>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4">
        <Alert type="information" title="Confirm" message="" className="w-full">
          <div className="flex flex-col gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-300 bg-blue-200 p-2 hover:bg-blue-300">
              <input
                id="checkbox"
                type="checkbox"
                onChange={(e) => setChecked(e.target.checked)}
              />
              Check to confirm you understand -{" "}
              <b>this message won't show after the migration</b>
            </label>
          </div>
        </Alert>
        <Button
          disabled={!checked}
          loading={migrateZones.isPending}
          onClick={() => migrateZones.mutate()}
          className="w-full"
          color="red"
        >
          Migrate
        </Button>
      </CardContent>
    </Card>
  );
};
